import { styled } from "@linaria/react";
import React, { type FC, type ReactNode, useState } from "react";
import { t } from "@/themes/theme.ts";
import type { ButtonBasicProps } from "@/components/buttons/shared.ts";

const IconButtonWrapper = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;

  height: 25px;

  width: 100%;
  max-width: max-content;
  gap: 10px;

  font-family: ${t.fontPrimary};
  font-size: var(--14px);
  // The fixed height can't fit wrapped text (min-content parents would
  // otherwise wrap the label)
  white-space: nowrap;

  padding: 8px 16px;

  border: none;
  cursor: pointer;

  border-radius: ${t.buttonBorderRadius};

  transition:
    opacity 0.3s,
    background-color 0.3s,
    scale 0.5s ${t.transition};

  background-color: var(--background-color);
  color: var(--text-color);

  &:disabled {
    background-color: var(--disabled-background-color);
    color: var(--disabled-text-color);
    cursor: not-allowed;
  }

  &[data-no-padding="true"] {
    padding: 0;
  }

  &[data-flare="true"] {
    &:hover {
      scale: 105%;
    }

    &:active {
      scale: 95%;
      opacity: 0.8;
    }

    &:disabled {
      scale: 1;
    }
  }
`;

const IconWrapper = styled.div`
  flex-shrink: 0;
`;

export interface ButtonIconProps extends ButtonBasicProps {
  icon: ReactNode;
  text: string;
  backgroundColor?: string;
  disabledBackgroundColor?: string;
  textColor?: string;
  disabledTextColor?: string;
  disabledOverride?: boolean;
  disabledTooltipText?: string;
  flare?: boolean;
  noPadding?: boolean;
}

export const ButtonIcon: FC<ButtonIconProps> = ({
  icon,
  onClick,
  text,
  backgroundColor,
  textColor,
  disabledBackgroundColor,
  disabledTextColor,
  disabledOverride,
  tooltip,
  flare,
  noPadding,
}) => {
  const [disabled, setDisabled] = useState(false);

  return (
    <IconButtonWrapper
      onClick={(e) => {
        if (disabled || !onClick) return;

        e.preventDefault();
        e.stopPropagation();

        setDisabled(true);

        const finished = () => {
          setDisabled(false);
        };

        onClick(finished);
      }}
      style={
        {
          "--background-color": backgroundColor ?? "none",
          "--text-color": textColor ?? "white",
          "--disabled-background-color": disabledBackgroundColor ?? backgroundColor ?? "none",
          "--disabled-text-color": disabledTextColor ?? textColor ?? "white",
        } as React.CSSProperties
      }
      disabled={disabledOverride || disabled}
      data-flare={flare}
      data-tooltip={tooltip}
      data-direction={"top"}
      data-no-padding={noPadding}
    >
      <IconWrapper>{icon}</IconWrapper>
      {text}
    </IconButtonWrapper>
  );
};
